import { Card, Row, Col, Statistic, Progress } from "antd";
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import type { DashboardMetrics } from "../types";

interface DashboardOverviewProps {
  data: DashboardMetrics;
}

export const DashboardOverview = ({ data }: DashboardOverviewProps) => {
  const applicationTrends = data?.recruitment_activity?.application_trends?.trend_data || [];
  
  const genderData = Object.entries(data?.diversity_metrics?.shortlisted?.gender_distribution || {})
    .map(([name, value]) => ({ name, value }));

  const recruiterPerformance = data?.process_efficiency?.recruiter_productivity?.slice(0, 5) || [];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const metrics = [
    {
      title: 'Monthly Applications',
      value: data?.recruitment_activity?.application_trends?.monthly || 0,
      color: '#3B82F6',
    },
    {
      title: 'Active Jobs',
      value: data?.recruitment_activity?.active_job_postings || 0,
      color: '#10B981',
    },
    {
      title: 'Hires This Month',
      value: data?.monthly_metrics?.hires || 0,
      color: '#8B5CF6',
    },
    {
      title: 'Team Productivity',
      value: `${calculateAverageProductivity(data)}%`,
      color: '#F59E0B',
    },
  ];

  function calculateAverageProductivity(data: DashboardMetrics): number {
    const recruiters = data?.process_efficiency?.recruiter_productivity || [];
    if (recruiters.length === 0) return 0;
    
    const avg = recruiters.reduce((sum, recruiter) => {
      const hiresScore = recruiter.talents_hired * 10;
      const interviewScore = recruiter.interviews_scheduled * 5;
      const contactScore = recruiter.talents_contacted * 2;
      return sum + hiresScore + interviewScore + contactScore;
    }, 0) / recruiters.length;
    
    return Math.min(Math.round(avg), 100);
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <Row gutter={[16, 16]}>
        {metrics.map((metric, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card className="h-full">
              <Statistic
                title={metric.title}
                value={metric.value}
                valueStyle={{ color: metric.color, fontSize: '28px' }}
              />              
            </Card>
          </Col>
        ))}
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Application Trends">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={applicationTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="applications" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card title="Gender Diversity (Shortlisted)">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={genderData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {genderData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Performance Metrics */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Top Performers">
            <div className="space-y-4">
              {recruiterPerformance.map((recruiter, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">{recruiter.name}</div>
                    <div className="text-sm text-gray-500">{recruiter.email}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">{recruiter.talents_hired} hires</div>
                    <Progress 
                      percent={Math.min(recruiter.talents_hired * 10, 100)} 
                      size="small" 
                      showInfo={false}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card title="Recruitment Funnel">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>Applications Received</span>
                <span className="font-bold">
                  {data?.recruitment_activity?.application_trends?.monthly || 0}
                </span>
              </div>
              <Progress percent={100} strokeColor="#e5e7eb" />
              
              <div className="flex justify-between items-center">
                <span>Shortlisted</span>
                <span className="font-bold">
                  {data?.recruitment_process?.pipeline_status?.talents_shortlisted || 0}
                </span>
              </div>
              <Progress 
                percent={
                  data?.recruitment_activity?.application_trends?.monthly ?
                  Math.round((data.recruitment_process.pipeline_status.talents_shortlisted / 
                    data.recruitment_activity.application_trends.monthly) * 100) : 0
                } 
                strokeColor="#93c5fd"
              />
              
              <div className="flex justify-between items-center">
                <span>Interviews</span>
                <span className="font-bold">
                  {data?.recruitment_activity?.pre_chats?.scheduled || 0}
                </span>
              </div>
              <Progress 
                percent={
                  data?.recruitment_process?.pipeline_status?.talents_shortlisted ?
                  Math.round((data.recruitment_activity.pre_chats.scheduled / 
                    data.recruitment_process.pipeline_status.talents_shortlisted) * 100) : 0
                } 
                strokeColor="#60a5fa"
              />
              
              <div className="flex justify-between items-center">
                <span>Hires</span>
                <span className="font-bold">
                  {data?.monthly_metrics?.hires || 0}
                </span>
              </div>
              <Progress 
                percent={
                  data?.recruitment_activity?.pre_chats?.scheduled ?
                  Math.round((data.monthly_metrics.hires / 
                    data.recruitment_activity.pre_chats.scheduled) * 100) : 0
                } 
                strokeColor="#3b82f6"
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};